import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { patientManager } from "@core/service-container";
import { Disease, FunctionalStatus, functionalStatuses } from "@domain/medical-condition/medical-condition";
import { useFormatMedicalCondition, useSortedDiseaseList } from "@domain/medical-condition/use-medical-condition";
import { isPendingPatient, Patient, PendingPatient } from "@domain/patient/patient";
import { sendAnalyticsEventEditPatientDetails } from "@domain/patient/patient-analytics";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { ErrorMessage } from "@ui/components/common/error-message";
import { ToastContext } from "@ui/components/common/toast-provider";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { DialogProps } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import React, { useContext } from "react";
import { Controller, useForm } from "react-hook-form";

export interface EditPatientInfoDialogProps extends DialogProps {
	patient: PendingPatient | Patient;
}

interface FormData {
	functionalStatus?: FunctionalStatus;
	disease?: Disease;
}

export const EditPatientInfoDialog: React.FC<EditPatientInfoDialogProps> = ({ patient, ...otherProps }) => {
	const { format } = useI18n();
	const { formatFunctionalStatus } = useFormatMedicalCondition();
	const { showToast } = useContext(ToastContext);
	const { formState, control, handleSubmit } = useForm<FormData>({
		defaultValues: {
			functionalStatus: patient.functionalStatus,
			disease: !isPendingPatient(patient) ? patient.disease : undefined,
		},
	});

	const [submitPatientInfo, isLoading, isError] = useAsyncOperation(async (data: FormData) => {
		sendAnalyticsEventEditPatientDetails(patient, data);
		await patientManager.editPatientDetails(patient.id, data.disease, data.functionalStatus);
		showToast(format("patientDetails.column.editInfoDialog.successToast"), "", "success");
		otherProps.onHide();
	}, {});

	const diseases = useSortedDiseaseList();

	return (
		<BaseDialog
			header={format("patientDetails.column.editInfoDialog.header")}
			footer={
				<ButtonContainer>
					<Button
						loading={isLoading}
						type="submit"
						disabled={!formState.isDirty}
						onClick={handleSubmit(submitPatientInfo)}
					>
						{format("global.dialog.save")}
					</Button>
				</ButtonContainer>
			}
			width={DIALOG_SIZE.M}
			{...otherProps}
		>
			<Form onSubmit={handleSubmit(submitPatientInfo)}>
				<FormField>
					<label className="p-size-m">{format("patientDetails.column.editInfoDialog.medicalCondition")}</label>
					<Controller
						name="disease"
						control={control}
						render={({ field, fieldState }) => (
							<>
								<StyledDropdown
									disabled={isPendingPatient(patient)}
									options={diseases}
									placeholder={format("global.form.dropdown.placeholder")}
									{...field}
									optionLabel="label"
									optionValue="value"
									appendTo="self"
								/>
								<ErrorMessage>{isError || fieldState.error?.message}</ErrorMessage>
							</>
						)}
					/>
				</FormField>

				<FormField>
					<label className="p-size-m">{format("patientDetails.column.editInfoDialog.functionalStatus")}</label>
					<Controller
						name="functionalStatus"
						control={control}
						render={({ field }) => (
							<StyledDropdown
								options={functionalStatuses.map(type => ({
									value: type,
									label: formatFunctionalStatus(type),
								}))}
								placeholder={format("global.form.dropdown.placeholder")}
								{...field}
								optionLabel="label"
								optionValue="value"
								appendTo="self"
							/>
						)}
					/>
				</FormField>
			</Form>
		</BaseDialog>
	);
};

const ButtonContainer = styled.div`
	${row(24, "flexend")}
`;

const Form = styled.form`
	${stack("M", "flexstart", "stretch")}
	padding-bottom: 100px;
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;

const StyledDropdown = styled(Dropdown)`
	@media ${physioMeMobileBreakpoint} {
		.p-dropdown-item {
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
			width: calc(100vw - 48px);
		}
	}
`;
